import React from 'react';
import { amount as fmtCurrencyAmount } from '../../lib/money';
import { Badge } from '../../ui/components';
import { Icon } from '../../ui/icons';
import { useT } from '../../i18n';
import './approvals.css';

const STEP_META = {
  Approved: { tone: 'success', icon: <Icon.Check size={13} />, label: 'Approved' },
  Rejected: { tone: 'danger', icon: <Icon.X size={13} />, label: 'Rejected' },
  Pending: { tone: 'warning', icon: <Icon.Clock size={13} />, label: 'Pending' },
  Skipped: { tone: 'default', icon: <Icon.Minus size={13} />, label: 'Skipped' },
};

const fmtAmount = (amount, currency) => fmtCurrencyAmount(currency, amount);

/**
 * Renders a document's snapshotted approval chain as a vertical stepper.
 *
 * @param steps            array of { order, roleName, minAmount, status, decidedByName, decidedAt, comment, signature }
 * @param currentStepOrder order of the step awaiting a decision (highlighted)
 * @param currency         currency used for the threshold display
 * @param showSignatures   when true, renders captured signature images
 */
export default function ApprovalChain({ steps = [], currentStepOrder = null, currency, showSignatures = false }) {
  const t = useT();
  if (!steps.length) return null;

  return (
    <ol className="appr-chain">
      {steps.map((s) => {
        const meta = STEP_META[s.status] || STEP_META.Pending;
        const isCurrent = s.status === 'Pending' && s.order === currentStepOrder;
        const roleLabel = s.roleName || t('Any approver');
        return (
          <li key={s.order} className={`appr-chain__step is-${meta.tone}${isCurrent ? ' is-current' : ''}`}>
            <div className="appr-chain__rail">
              <span className="appr-chain__dot">{meta.icon}</span>
            </div>
            <div className="appr-chain__body">
              <div className="appr-chain__head">
                <span className="appr-chain__role">
                  {t('Step {order}: {role}', { order: s.order, role: roleLabel })}
                </span>
                <Badge tone={meta.tone} dot>{isCurrent ? t('Awaiting') : t(meta.label)}</Badge>
              </div>
              <div className="appr-chain__meta">
                {Number(s.minAmount) > 0 && <span>{t('Triggered ≥ {amount}', { amount: fmtAmount(s.minAmount, currency) })}</span>}
                {s.decidedByName && (
                  <span>
                    {s.status === 'Rejected' ? t('Rejected by') : t('Approved by')} {s.decidedByName}
                    {s.onBehalfOfName ? ` ${t('(on behalf of {name})', { name: s.onBehalfOfName })}` : ''}
                    {s.decidedAt ? ` · ${new Date(s.decidedAt).toLocaleString()}` : ''}
                  </span>
                )}
              </div>
              {s.comment && <p className="appr-chain__comment">“{s.comment}”</p>}
              {showSignatures && s.decidedByName && (
                <div className="appr-chain__sign">
                  {s.signature
                    ? <img src={s.signature} alt={t('{name} signature', { name: s.decidedByName })} />
                    : <span className="appr-chain__unsigned">{t('— not signed —')}</span>}
                  <span className="appr-chain__sign-name">{s.decidedByName}</span>
                </div>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
